/**
 * IMAGE COMPRESSION SERVICE
 * =========================
 * Rule:
 * - If long side > 4000 px: resize down to 4000 px (dimension + quality compress).
 * - If file size > 10 MB but long side <= 4000 px: re-encode at same resolution (quality-only compress).
 * - With watermark: routes to companion for single-pass compress + watermark overlay.
 */

import heic2any from "heic2any";
import piexif from "piexifjs";
import { compressImageViaCompanion, checkCompanion } from "./videoCompressionService";

/**
 * Read a File/Blob as a base64 data URL.
 */
const readAsDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

/**
 * Extract EXIF from a JPEG file. Returns the EXIF bytes string, or null if not available.
 */
const extractExif = async (file) => {
  try {
    if (file.type !== "image/jpeg" && !/\.jpe?g$/i.test(file.name)) return null;
    const dataUrl = await readAsDataURL(file);
    const exifObj = piexif.load(dataUrl);
    // Check if there's any meaningful EXIF data
    const hasData = Object.keys(exifObj).some(
      (ifd) => ifd !== "thumbnail" && Object.keys(exifObj[ifd] || {}).length > 0
    );
    if (!hasData) return null;
    return piexif.dump(exifObj);
  } catch {
    return null;
  }
};

/**
 * Create a minimal EXIF bytes string with DateTimeOriginal/DateTime set from a JS date/timestamp.
 * Used when the source file has no EXIF (e.g. PNG screenshots, HEIC without embedded EXIF).
 */
const createMinimalExif = (dateOrTimestamp) => {
  try {
    const d = new Date(dateOrTimestamp);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    const dtStr = `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const exifObj = { "0th": {}, "Exif": {}, "GPS": {} };
    exifObj["Exif"][piexif.ExifIFD.DateTimeOriginal] = dtStr;
    exifObj["Exif"][piexif.ExifIFD.DateTimeDigitized] = dtStr;
    exifObj["0th"][piexif.ImageIFD.DateTime] = dtStr;
    return piexif.dump(exifObj);
  } catch {
    return null;
  }
};

/**
 * Inject EXIF bytes into a compressed JPEG blob. Returns a new File with metadata preserved.
 */
const injectExif = async (blob, exifBytes, fileName, lastModified) => {
  try {
    const dataUrl = await readAsDataURL(blob);
    const withExif = piexif.insert(exifBytes, dataUrl);
    // Convert data URL back to blob
    const binary = atob(withExif.split(",")[1]);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
    return new File([array], fileName, { type: "image/jpeg", lastModified });
  } catch {
    // If injection fails, return original blob as-is
    return new File([blob], fileName, { type: "image/jpeg", lastModified });
  }
};

/** Rule: keep long side <= 4000px */
const MAX_LONG_SIDE = 4000;

/** Rule trigger: file size > 10 MB */
const SIZE_TRIGGER_BYTES = 10 * 1024 * 1024;

/** Fixed quality used only when a resize actually happens */
const RESIZE_JPEG_QUALITY = 0.92;
const MIN_NO_REGRESSION_QUALITY = 0.62;
const NO_REGRESSION_QUALITY_STEP = 0.06;

const canvasToJpegBlob = (canvas, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });

const getImageLongSide = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(Math.max(img.width || 0, img.height || 0));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("failed to read image dimensions"));
    };
    img.src = url;
  });

const isCompressionRuleApplicable = async (file) => {
  if (file.size > SIZE_TRIGGER_BYTES) return true;
  try {
    const longSide = await getImageLongSide(file);
    return longSide > MAX_LONG_SIDE;
  } catch {
    // Conservative fallback: if dimensions cannot be probed, keep existing compression path.
    return true;
  }
};

const padJpegToExactSize = async (file, targetSizeBytes) => {
  if (file.size >= targetSizeBytes) return file;
  const padBytes = targetSizeBytes - file.size;
  const padding = new Uint8Array(padBytes);
  return new File([file, padding], file.name, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
};

/** Returns true if the file is HEIC/HEIF regardless of how the browser reports the MIME type */
const isHeic = (file) =>
  file.type === "image/heic" ||
  file.type === "image/heif" ||
  /\.heic$/i.test(file.name) ||
  /\.heif$/i.test(file.name);

export const needsCompression = (file) => {
  if (!file.type.startsWith("image/") && !isHeic(file)) return false;
  // GIFs are animated — don't flatten them
  if (file.type === "image/gif") return false;
  // Run through compressor to evaluate dimension-based rule from real image metadata.
  return true;
};

/**
 * Compress an image file using Canvas.
 * @param {File} file - The original image file
 * @param {(progress: number) => void} [onProgress] - Progress callback (0–100)
 * @returns {Promise<File>} - Compressed file (or original if compression enlarges it)
 */
export const compressImage = async (file, onProgress, { watermark = null } = {}) => {
  // Start with original, may become decoded JPEG for HEIC/HEIF.
  let sourceFile = file;

  // HEIC/HEIF: decode before any compression route (including companion watermark route)
  // so Studio never receives unsupported HEIC input.
  if (isHeic(file)) {
    try {
      console.log(`[ImageCompress] HEIC detected: "${file.name}" - decoding via heic2any...`);
      onProgress?.(5);
      const jpegBlob = await heic2any({ blob: file, toType: "image/jpeg", quality: 1 });
      const blob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
      const jpegName = file.name.replace(/\.hei[cf]$/i, ".jpg");
      sourceFile = new File([blob], jpegName, { type: "image/jpeg", lastModified: file.lastModified });
      console.log(`[ImageCompress] HEIC decoded: ${(file.size / 1024).toFixed(0)} KB -> ${(sourceFile.size / 1024).toFixed(0)} KB JPEG`);
    } catch (err) {
      const msg = err?.message || "HEIC decode failed";
      console.warn(`[ImageCompress] heic2any failed for "${file.name}":`, err);
      if (watermark?.WatermarkImageUrl) {
        throw new Error(`HEIC decode failed before watermark compression: ${msg}`);
      }
      return file;
    }
  }

  // ── Watermark routing ──────────────────────────────────────────────────────
  // If watermark is required, use companion for single-pass compress + overlay.
  // Fail fast if companion unavailable — no fallback to Canvas.
  if (watermark?.WatermarkImageUrl) {
    const companion = await checkCompanion();
    if (!companion) {
      throw new Error("Companion not available — cannot apply watermark to image. Please start the companion app.");
    }
    try {
      const ruleApplies = await isCompressionRuleApplicable(sourceFile);
      const exifBytes = await extractExif(sourceFile);
      const companionOut = await compressImageViaCompanion(sourceFile, { onProgress, watermark });

      // Companion FFmpeg re-encode strips EXIF; re-inject for JPEG sources when available.
      // For non-JPEG sources (PNG, etc.) with no EXIF, create minimal EXIF from file date.
      const effectiveExifBytes = exifBytes || createMinimalExif(file.lastModified);
      if (effectiveExifBytes) {
        const withExif = await injectExif(
          companionOut,
          effectiveExifBytes,
          companionOut.name || sourceFile.name.replace(/\.[^.]+$/, ".jpg"),
          file.lastModified
        );
        if (!ruleApplies) {
          const strictOut = await padJpegToExactSize(withExif, file.size);
          if (strictOut.size !== file.size) {
            console.warn(
              `[ImageCompress] Watermark-only strict mode could not keep exact size for "${file.name}" ` +
              `(${(file.size / 1024).toFixed(0)} KB -> ${(strictOut.size / 1024).toFixed(0)} KB)`
            );
          }
          return strictOut;
        }
        return withExif;
      }

      if (!ruleApplies) {
        const strictOut = await padJpegToExactSize(companionOut, file.size);
        if (strictOut.size !== file.size) {
          console.warn(
            `[ImageCompress] Watermark-only strict mode could not keep exact size for "${file.name}" ` +
            `(${(file.size / 1024).toFixed(0)} KB -> ${(strictOut.size / 1024).toFixed(0)} KB)`
          );
        }
        return strictOut;
      }

      return companionOut;
    } catch (err) {
      throw new Error(`Companion image watermark compression failed: ${err.message}`);
    }
  }

  // ── Canvas path (no watermark) ──────────────────────────────────────────────
  // Browser-based HEIC decoding + Canvas resizing (fast, no companion needed)

  return new Promise((resolve) => {
    onProgress?.(10);

    const t0 = performance.now();
    const img = new Image();
    const url = URL.createObjectURL(sourceFile);

    // Extract EXIF from the source JPEG before Canvas strips it
    // (sourceFile is the decoded JPEG for HEIC input, or the original JPEG otherwise)
    let exifBytesPromise = extractExif(sourceFile);

    img.onload = async () => {
      URL.revokeObjectURL(url);
      // Always compare against the originally selected file size (not decoded intermediates).
      const originalInputSize = file.size;
      onProgress?.(30);

      const { width, height } = img;

      const longSide = Math.max(width, height);
      const isOverSizeThreshold = originalInputSize > SIZE_TRIGGER_BYTES;
      const isOverDimensionThreshold = longSide > MAX_LONG_SIDE;
      const triggered = isOverSizeThreshold || isOverDimensionThreshold;

      // If nothing triggered, skip compression entirely.
      if (!triggered) {
        onProgress?.(100);
        // For HEIC input, sourceFile is the decoded JPEG — always return that.
        resolve(sourceFile);
        return;
      }

      // If dimensions exceed the limit, resize down; otherwise keep original dimensions
      // (size-triggered only: re-encode at same resolution to reduce file size).
      const scale = isOverDimensionThreshold ? MAX_LONG_SIDE / longSide : 1;
      const targetWidth = Math.max(1, Math.round(width * scale));
      const targetHeight = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        onProgress?.(100);
        resolve(sourceFile);
        return;
      }
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
      onProgress?.(70);

      let quality = RESIZE_JPEG_QUALITY;
      let blob = await canvasToJpegBlob(canvas, quality);

      if (!blob) {
        onProgress?.(100);
        resolve(file);
        return;
      }

      // Keep quality high by default, but if resized output is still larger than the original
      // selected file, lower quality gradually to avoid obvious size regressions.
      while (blob.size > originalInputSize && quality > MIN_NO_REGRESSION_QUALITY) {
        quality = Math.max(MIN_NO_REGRESSION_QUALITY, +(quality - NO_REGRESSION_QUALITY_STEP).toFixed(2));
        const nextBlob = await canvasToJpegBlob(canvas, quality);
        if (!nextBlob) break;
        blob = nextBlob;
      }

      onProgress?.(90);

      const compressedName = sourceFile.name.replace(/\.[^.]+$/, ".jpg");

      // Re-inject EXIF metadata into the compressed JPEG.
      // For sources with no EXIF (PNG screenshots, etc.), create minimal EXIF from file date.
      const exifBytes = await exifBytesPromise;
      const effectiveExifBytes = exifBytes || createMinimalExif(file.lastModified);
      let compressedFile;
      if (effectiveExifBytes) {
        compressedFile = await injectExif(blob, effectiveExifBytes, compressedName, file.lastModified);
        console.log(`[ImageCompress] EXIF metadata ${exifBytes ? "preserved" : "created from file date"} for "${file.name}"`);
      } else {
        compressedFile = new File([blob], compressedName, {
          type: "image/jpeg",
          lastModified: file.lastModified,
        });
      }

      onProgress?.(100);

      const elapsed = ((performance.now() - t0) / 1000).toFixed(2);
      const deltaPct = Math.round((Math.abs(compressedFile.size - originalInputSize) / originalInputSize) * 100);
      const sizeLabel = compressedFile.size <= originalInputSize
        ? `${deltaPct}% reduction`
        : `${deltaPct}% increase`;

      console.log(
        `[ImageCompress] "${file.name}" ${(file.size / 1024).toFixed(0)} KB → ` +
        `${(compressedFile.size / 1024).toFixed(0)} KB ` +
        `(${sizeLabel}) ` +
        `[${width}x${height} -> ${targetWidth}x${targetHeight}] q=${quality} in ${elapsed}s`
      );

      resolve(compressedFile);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      console.warn(`[ImageCompress] Failed to load "${file.name}", using original`);
      resolve(sourceFile); // Don't block upload — fall back to decoded source (or original)
    };

    img.src = url;
  });
};
